import { RootState } from "@/store/state";
import { MutationTree } from "vuex";
import { ActiveView } from "@/models/active-view";
import { SystemMode } from "@/models/system-mode";
import { ISlide } from "@/models/slide";
import { log } from "@/core/logger";
import { ICase } from "@/models/case";

export enum RootMutations {
  // System Mode
  SET_SYSTEM_MODE = "SET_SYSTEM_MODE",

  // Labeling
  SET_LABELING = "SET_LABELING",

  // Auth
  SET_LOGGED_IN_USERNAME = "SET_LOGGED_IN_USERNAME",

  // Router
  DISPLAY_STATS_SCREEN = "DISPLAY_STATS_SCREEN",
  DISPLAY_CASE_REPORT = "DISPLAY_CASE_REPORT",
  HIDE_STATS_SCREEN = "HIDE_STATS_SCREEN",
  RESET_ACTIVE_VIEW = "RESET_ACTIVE_VIEW",
  SET_QUERY_PARAMS = "SET_QUERY_PARAMS",
  CLEAR_QUERY_PARAMS = "CLEAR_QUERY_PARAMS",
  SET_ROUTER_PARAMS = "SET_ROUTER_PARAMS",

  // Slides
  UPDATE_CURRENT_SLIDE = "UPDATE_CURRENT_SLIDE",
  SET_ACTIVE_SLIDE_ID = "SET_ACTIVE_SLIDE_ID",
  SET_SLIDE = "SET_SLIDE",
  UPDATE_SLIDE = "UPDATE_SLIDE",

  // Cases
  REMOVE_CURRENT_CASE = "REMOVE_CURRENT_CASE",
  UPDATE_CASE = "UPDATE_CASE",
  SET_ACTIVE_CASE_ID = "SET_ACTIVE_CASE_ID",
  SET_FR_CASES = "SET_FR_CASES",
  SET_SR_CASES = "SET_SR_CASES",

  // Config
  SET_CONFIG = "SET_CONFIG",
  SET_INSIGHTS_CONFIG = "SET_INSIGHTS_CONFIG",
  SET_HEATMAPS_CONFIG = "SET_HEATMAPS_CONFIG",

  // Error
  SET_ERROR = "SET_ERROR",
}

export const mutations: MutationTree<RootState> = {
  // System Mode

  [RootMutations.SET_SYSTEM_MODE](state: RootState, payload: { systemMode: string }): void {
    state.systemMode = SystemMode[payload.systemMode as keyof typeof SystemMode];
  },

  // Labeling

  [RootMutations.SET_LABELING](state: RootState, payload: { labeling: object }): void {
    state.labeling = payload.labeling;
  },

  // Auth

  [RootMutations.SET_LOGGED_IN_USERNAME](state: RootState, payload: { userName: string }) {
    state.loggedInUserName = payload.userName;
  },

  // Router

  [RootMutations.DISPLAY_STATS_SCREEN](state: RootState): void {
    state.activeView = ActiveView.Statistics;
  },

  [RootMutations.DISPLAY_CASE_REPORT](state: RootState): void {
    state.activeView = ActiveView.CaseReport;
  },

  [RootMutations.HIDE_STATS_SCREEN](state: RootState): void {
    state.activeView = ActiveView.None;
  },

  [RootMutations.RESET_ACTIVE_VIEW](state: RootState): void {
    state.activeView = ActiveView.None;
  },

  [RootMutations.SET_QUERY_PARAMS](state: RootState, queryParams: URLSearchParams): void {
    state.queryParams = queryParams;
  },

  [RootMutations.SET_ROUTER_PARAMS](state: RootState, routerParams: Object): void {
    state.routeParams = routerParams;
  },

  [RootMutations.CLEAR_QUERY_PARAMS](state: RootState) {
    state.queryParams = new URLSearchParams();
  },

  // Slides

  [RootMutations.SET_ACTIVE_SLIDE_ID](state: RootState, payload: { activeSlideId: string }) {
    log("Setting active slide id to", payload.activeSlideId);
    state.activeSlideId = payload.activeSlideId;
  },

  [RootMutations.UPDATE_CURRENT_SLIDE](state: RootState, payload: any) {
    // calculate minimap dimensions based on a base size of 90 and the image ratio
    let minimapDimensions: [number, number];
    let imageRatio = payload.data.dimensions[0] / payload.data.dimensions[1];
    let baseSize = 90;
    if (imageRatio < 1) {
      minimapDimensions = [baseSize, baseSize / imageRatio];
    } else {
      minimapDimensions = [baseSize * imageRatio, baseSize];
    }
    state.currentSlide = {
      id: payload.data.slide_id,
      name: payload.data.logical_name,
      dimensions: payload.data.dimensions,
      minimapDimensions,
      stain: payload.data.stain,
      tissueType: payload.data.sub_tissue
        ? payload.data.sub_tissue.toLowerCase()
        : payload.data.tissue_type.toLowerCase(),
      heatmaps: payload.data.algorithm_results,
      POIs: payload.data.pois,
    };
    state.activeHeatmaps = [];
    state.isQuantificationLegend = false;
  },

  [RootMutations.SET_SLIDE](state: RootState, payload: { updatedSlide: ISlide }) {
    state.slides = state.slides.map((slide) => {
      if (slide.slideId === payload.updatedSlide.slideId) {
        return payload.updatedSlide;
      } else {
        return slide;
      }
    });
  },

  [RootMutations.UPDATE_SLIDE](
    state: RootState,
    payload: { slideId: string; updates: Object }
  ): void {
    log(`updating slide ${payload.slideId} with`, payload.updates);
    state.slides = state.slides.map((slide) => {
      if (slide.slideId === payload.slideId) {
        return { ...slide, ...payload.updates };
      } else {
        return slide;
      }
    });
  },

  // Cases

  [RootMutations.REMOVE_CURRENT_CASE](state: RootState) {
    state.activeCaseId = "";
    state.slides = [];
    state.activeHeatmaps = [];
  },

  [RootMutations.UPDATE_CASE](
    state: RootState,
    payload: { caseId: string; updates: Object }
  ): void {
    log(`updating case ${payload.caseId} with`, payload.updates);
    state.cases = state.cases.map((caseObj) => {
      if (caseObj.caseId === payload.caseId) {
        return { ...caseObj, ...payload.updates };
      } else {
        return caseObj;
      }
    });
  },

  [RootMutations.SET_ACTIVE_CASE_ID](state: RootState, payload: { id: string }) {
    state.activeCaseId = payload.id;
  },

  [RootMutations.SET_SR_CASES](state: RootState, payload: { cases: ICase[] }) {
    state.cases = payload.cases;
  },

  [RootMutations.SET_FR_CASES](state: RootState, payload: { cases: ICase[]; caseLabels: any }) {
    state.cases = payload.cases.map((caseObj: ICase) => {
      return {
        ...caseObj,
        caseLabels: payload.caseLabels[caseObj.caseId],
      };
    });
  },

  // Config
  [RootMutations.SET_CONFIG](state: RootState, payload: { config: any }): void {
    state.config = payload.config;
  },

  [RootMutations.SET_INSIGHTS_CONFIG](state: RootState, payload: { insightsConfig: any }): void {
    state.insightsConfig = payload.insightsConfig;
  },

  [RootMutations.SET_HEATMAPS_CONFIG](state: RootState, payload: { heatmapsConfig: any }): void {
    state.heatmapsConfig = payload.heatmapsConfig;
  },

  // Error
  [RootMutations.SET_ERROR](state: RootState): void {
    state.isError = true;
  },
};
